import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { CollectionVisibility } from "@poi-planner/shared";
import { useCollectionsStore } from "../store/collectionsStore";
import { canEditCollection } from "../utils/collectionAccess";
import { Button } from "./Button";
import { FormInput } from "./FormFields";

const VISIBILITY: Record<
  CollectionVisibility,
  { icon: string; label: string }
> = {
  private: { icon: "lock", label: "Private" },
  shared: { icon: "group", label: "Shared" },
  public: { icon: "public", label: "Public" },
};

function VisibilityLabel({ visibility }: { visibility: CollectionVisibility }) {
  const { icon, label } = VISIBILITY[visibility];
  return (
    <span className="inline-flex items-center gap-0.5 text-xs text-tertiary">
      <span className="material-symbols-outlined icon-match-text" aria-hidden>
        {icon}
      </span>
      {label}
    </span>
  );
}

/**
 * A POI's collection memberships with an inline picker to add it to another
 * collection or create one. Changes apply immediately (collections are saved
 * through their own API, not the POI's Save).
 */
export function PoiCollectionsEditor({
  poiId,
  pickerOpen,
  onPickerOpenChange,
  disabled = false,
}: {
  poiId: string;
  pickerOpen: boolean;
  onPickerOpenChange: (open: boolean) => void;
  disabled?: boolean;
}) {
  const collections = useCollectionsStore((state) => state.collections);
  const addPoisToCollection = useCollectionsStore(
    (state) => state.addPoisToCollection,
  );
  const removePoisFromCollection = useCollectionsStore(
    (state) => state.removePoisFromCollection,
  );
  const createCollection = useCollectionsStore(
    (state) => state.createCollection,
  );
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const memberships = collections.filter((c) => c.poiIds.includes(poiId));
  const trimmedQuery = query.trim();
  const candidates = collections.filter(
    (c) =>
      canEditCollection(c) &&
      !c.poiIds.includes(poiId) &&
      c.name.toLowerCase().includes(trimmedQuery.toLowerCase()),
  );
  const canCreate =
    !!trimmedQuery &&
    !collections.some(
      (c) => c.name.toLowerCase() === trimmedQuery.toLowerCase(),
    );

  useEffect(() => {
    if (!pickerOpen) return;
    setQuery("");
    setError(null);
    pickerRef.current?.querySelector("input")?.focus();
    // Show the whole picker, including "Create …", not just its input.
    pickerRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [pickerOpen]);

  const run = async (action: () => Promise<unknown>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (err: unknown) {
      console.error(failure, err);
      setError(failure);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (collectionId: string) => {
    if (
      await run(
        () => addPoisToCollection(collectionId, [poiId]),
        "Couldn't add to that collection. Try again.",
      )
    ) {
      onPickerOpenChange(false);
    }
  };

  const create = async () => {
    if (
      await run(async () => {
        const id = await createCollection(trimmedQuery);
        await addPoisToCollection(id, [poiId]);
      }, "Couldn't create the collection. Try again.")
    ) {
      onPickerOpenChange(false);
    }
  };

  const handlePickerKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      // Close the picker, not the dialog.
      event.preventDefault();
      event.stopPropagation();
      onPickerOpenChange(false);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (candidates[0]) void add(candidates[0].id);
      else if (canCreate) void create();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {memberships.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {memberships.map((collection) => (
            <li
              key={collection.id}
              className="flex items-center gap-2.5 rounded-lg border border-border py-2 pl-3 pr-1.5"
            >
              <span
                className="material-symbols-outlined text-lg text-tertiary"
                aria-hidden
              >
                folder
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-primary">
                  {collection.name}
                </div>
                <VisibilityLabel visibility={collection.visibility} />
              </div>
              {!disabled && canEditCollection(collection) && (
                <Button
                  variant="icon"
                  size="sm"
                  icon="close"
                  disabled={busy}
                  aria-label={`Remove from ${collection.name}`}
                  onClick={() =>
                    void run(
                      () => removePoisFromCollection(collection.id, [poiId]),
                      "Couldn't remove it from that collection. Try again.",
                    )
                  }
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {pickerOpen && !disabled && (
        <div
          ref={pickerRef}
          className="flex flex-col gap-1 rounded-xl border border-border p-2"
        >
          <FormInput
            value={query}
            onChange={setQuery}
            onKeyDown={handlePickerKeyDown}
            placeholder="Find or create a collection"
            aria-label="Find or create a collection"
            disabled={busy}
          />
          {candidates.map((collection) => (
            <Button
              key={collection.id}
              variant="unstyled"
              disabled={busy}
              onClick={() => void add(collection.id)}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-subtle"
            >
              <span
                className="material-symbols-outlined text-lg text-tertiary"
                aria-hidden
              >
                folder
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-primary">
                {collection.name}
              </span>
              <VisibilityLabel visibility={collection.visibility} />
            </Button>
          ))}
          {canCreate && (
            <Button
              variant="unstyled"
              disabled={busy}
              onClick={() => void create()}
              className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm font-semibold text-info hover:bg-subtle"
            >
              <span className="material-symbols-outlined text-lg" aria-hidden>
                create_new_folder
              </span>
              Create “{trimmedQuery}”
            </Button>
          )}
          {!candidates.length && !canCreate && (
            <p className="m-0 px-2 py-1.5 text-xs text-tertiary">
              {trimmedQuery
                ? "Already in that collection."
                : "Already in every collection you can edit. Type a name to create one."}
            </p>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="m-0 text-xs font-semibold text-error">
          {error}
        </p>
      )}

      {!pickerOpen && !disabled && (
        <div>
          <Button
            variant="ghost"
            size="sm"
            icon="add"
            onClick={() => onPickerOpenChange(true)}
            className="-ml-2"
          >
            Add to collection
          </Button>
        </div>
      )}
    </div>
  );
}
